import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/apiBase';
import {
  getAuthConfig,
  getStoredToken,
  isEntraEnabled,
  isLocalLoginEnabled,
  signOut,
} from '../services/authSession';

const ROLE_LABELS = {
  super_admin: 'Super admin',
  consultant: 'Consultant',
  client_admin: 'Client admin',
  client_viewer: 'Client viewer',
};

// Which roles each role may hand out. Decided 2026-09-09: Chess performs
// every invitation, so only super admins invite (mirrors auth/roles.py).
const ASSIGNABLE = {
  super_admin: ['super_admin', 'consultant', 'client_admin', 'client_viewer'],
};

const formatWhen = (value) => {
  if (!value) return 'never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const roleBadge = (role) =>
  role === 'super_admin' || role === 'consultant'
    ? 'bg-blue-100 text-blue-800'
    : role === 'client_admin'
      ? 'bg-purple-100 text-purple-800'
      : 'bg-green-100 text-green-800';

const readError = async (response, fallback) => {
  const raw = await response.text();
  if (!raw) return fallback;
  try {
    const data = JSON.parse(raw);
    if (Array.isArray(data.detail)) return data.detail.map((d) => d.msg).join('; ');
    return data.detail || data.message || fallback;
  } catch (_) {
    return raw;
  }
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [invite, setInvite] = useState({
    email: '',
    full_name: '',
    client_id: '',
    role: 'client_viewer',
    client_ids: [],
    password: '',
  });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [events, setEvents] = useState([]);

  const entra = isEntraEnabled();
  const localLogin = isLocalLoginEnabled();
  const isSuperAdmin = user && user.role === 'super_admin';
  const canInvite = isSuperAdmin;
  const canViewUsers = user && (user.role === 'super_admin' || user.role === 'client_admin');

  const fetchUserData = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      window.location.href = '/login';
      return;
    }

    try {
      const userResponse = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) {
        if (userResponse.status === 401) {
          signOut();
          return;
        }
        throw new Error(await readError(userResponse, 'Failed to fetch user data'));
      }

      const userData = await userResponse.json();
      setUser(userData);
      setInvite((prev) => ({ ...prev, client_id: prev.client_id || userData.client_id || '' }));

      // Clients this user may work on (all for super admins).
      try {
        const clientsResponse = await fetch(`${API}/auth/my-clients`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (clientsResponse.ok) setClients(await clientsResponse.json());
      } catch (err) {
        console.error('[ProfilePage] Error loading clients:', err);
      }

      if (userData.role === 'super_admin' || userData.role === 'client_admin') {
        try {
          const usersResponse = await fetch(`${API}/auth/users`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (usersResponse.ok) setUsers(await usersResponse.json());
        } catch (err) {
          console.error('[ProfilePage] Error loading users:', err);
        }
      }

      // Recent security events: who signed in, who was invited, what was
      // activated. Super admins only (the endpoint enforces it too).
      if (userData.role === 'super_admin') {
        try {
          const eventsResponse = await fetch(`${API}/auth/audit-log?limit=50`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (eventsResponse.ok) setEvents((await eventsResponse.json()).events || []);
        } catch (err) {
          console.error('[ProfilePage] Error loading audit log:', err);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteResult(null);
    setInviteBusy(true);
    try {
      const token = getStoredToken();
      const useInvite = entra;
      const payload = {
        email: invite.email.trim(),
        full_name: invite.full_name.trim() || null,
        client_id: invite.client_id,
        role: invite.role,
        client_ids: invite.role === 'consultant' ? invite.client_ids : [],
      };
      if (!useInvite) payload.password = invite.password;
      const response = await fetch(`${API}/auth/${useInvite ? 'invite' : 'register'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await readError(response, 'Could not create the user'));
      }
      const created = await response.json();
      setInviteResult({ created, viaInvite: useInvite });
      setInvite((prev) => ({ ...prev, email: '', full_name: '', password: '', client_ids: [] }));
      fetchUserData();
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const toggleExtraClient = (id) => {
    setInvite((prev) => ({
      ...prev,
      client_ids: prev.client_ids.includes(id)
        ? prev.client_ids.filter((c) => c !== id)
        : [...prev.client_ids, id],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">Loading your profile...</div>
          <div className="text-sm text-gray-600">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Error Loading Profile</h2>
          <p className="text-red-800 mb-4">{error}</p>
          <button
            onClick={() => signOut()}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-semibold mb-2">No user data found</div>
          <button
            onClick={() => signOut()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-4"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const assignableRoles = ASSIGNABLE[user.role] || [];
  const authConfig = getAuthConfig();

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Profile &amp; Users</h1>
              <p className="text-gray-600">
                Signed in with {user.auth_provider === 'entra' ? 'single sign-on' : 'an IncurredIQ password'} ·{' '}
                {authConfig.environment} environment
              </p>
            </div>
            <div className="flex gap-3">
              <a href="/" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                Back to Dashboard
              </a>
              <button
                onClick={() => signOut()}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Your Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{user.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Full Name</p>
              <p className="font-medium">{user.full_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Role</p>
              <p className="font-medium">
                <span className={`px-2 py-1 rounded text-sm ${roleBadge(user.role)}`}>
                  {ROLE_LABELS[user.role] || user.role}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Home client</p>
              <p className="font-medium text-xs">{user.client_id}</p>
              {user.client_ids && user.client_ids.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">Also assigned: {user.client_ids.join(', ')}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-medium">
                <span
                  className={`px-2 py-1 rounded text-sm ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Sign-in</p>
              <p className="font-medium text-sm">
                {user.auth_provider === 'entra'
                  ? user.entra_linked
                    ? 'Single sign-on (linked)'
                    : 'Single sign-on (invitation pending)'
                  : 'IncurredIQ password'}
              </p>
            </div>
          </div>
        </div>

        {/* Invite / create user (Chess super admins only) */}
        {canInvite && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-1">{entra ? 'Invite a user' : 'Create a user'}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {entra
                ? 'They will sign in with their work email (Microsoft 365, Google Workspace, or an emailed code). Access is granted only to the email you enter here.'
                : 'Password sign-in is enabled on this deployment; set an initial password for them.'}
            </p>

            {inviteError && (
              <div className="rounded-md bg-red-50 p-3 mb-4 text-sm text-red-800">{inviteError}</div>
            )}
            {inviteResult && (
              <div className="rounded-md bg-green-50 p-3 mb-4 text-sm text-green-800">
                {inviteResult.viaInvite ? 'Invitation created for' : 'User created:'}{' '}
                <strong>{inviteResult.created.email}</strong>
                {inviteResult.viaInvite && (
                  <>
                    . Ask them to open <code>{window.location.origin}/login</code>, choose{' '}
                    <em>Sign in with your work email</em>, and the first time pick{' '}
                    <em>No account? Create one</em> using that exact email address.
                  </>
                )}
              </div>
            )}

            <form onSubmit={submitInvite} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-gray-700">Email</span>
                <input
                  type="email"
                  required
                  value={invite.email}
                  onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Full name</span>
                <input
                  type="text"
                  value={invite.full_name}
                  onChange={(e) => setInvite({ ...invite, full_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Role</span>
                <select
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value, client_ids: [] })}
                  className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                >
                  {assignableRoles.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">{invite.role === 'consultant' ? 'Home client' : 'Client'}</span>
                {user.role === 'super_admin' ? (
                  <select
                    required
                    value={invite.client_id}
                    onChange={(e) => setInvite({ ...invite, client_id: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">Select a client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.display_name || c.name} ({c.id})
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    readOnly
                    value={invite.client_id}
                    className="mt-1 block w-full border border-gray-200 bg-gray-50 rounded px-3 py-2 text-gray-600"
                  />
                )}
              </label>
              {!entra && localLogin && (
                <label className="block text-sm">
                  <span className="text-gray-700">Initial password</span>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={invite.password}
                    onChange={(e) => setInvite({ ...invite, password: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded px-3 py-2"
                  />
                  <span className="text-xs text-gray-500">At least 8 characters, one uppercase letter, one digit.</span>
                </label>
              )}
              {invite.role === 'consultant' && user.role === 'super_admin' && (
                <div className="md:col-span-2 text-sm">
                  <span className="text-gray-700">Additional assigned clients</span>
                  <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-1">
                    {clients
                      .filter((c) => c.id !== invite.client_id)
                      .map((c) => (
                        <label key={c.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={invite.client_ids.includes(c.id)}
                            onChange={() => toggleExtraClient(c.id)}
                          />
                          <span>{c.display_name || c.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={inviteBusy || !invite.client_id}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {inviteBusy ? 'Saving…' : entra ? 'Send invitation' : 'Create user'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users list */}
        {canViewUsers && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-1">Users ({users.length})</h2>
            <p className="text-sm text-gray-600 mb-4">
              {isSuperAdmin
                ? 'Everyone with access to this environment, the client they are tied to, and when they last signed in.'
                : 'People at your organisation with access. To add or remove someone, contact Chess Consulting.'}
            </p>
            {users.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No users found</p>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div key={u.id} className="border rounded p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{u.email}</p>
                        {u.full_name && <p className="text-sm text-gray-600">{u.full_name}</p>}
                        <p className="text-xs text-gray-500 mt-1">
                          {u.client_id}
                          {u.client_ids && u.client_ids.length > 0 && ` +${u.client_ids.length} more`}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-sm ${roleBadge(u.role)}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {u.is_active ? '✓ Active' : '✗ Inactive'}
                          {u.auth_provider === 'entra' &&
                            (u.entra_linked ? ' · single sign-on' : ' · invitation pending')}
                        </p>
                        <p className="text-xs text-gray-500">Last sign-in: {formatWhen(u.last_login)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent security events (super admins) */}
        {isSuperAdmin && (
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h2 className="text-xl font-semibold mb-1">Recent security events</h2>
            <p className="text-sm text-gray-600 mb-4">
              Sign-ins, invitations, account changes, identity links, and activations, newest first. Every entry records who, when, and from where.
            </p>
            {events.length === 0 ? (
              <p className="text-gray-500 text-center py-6">No events recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500 border-b">
                      <th className="py-2 pr-4">When</th>
                      <th className="py-2 pr-4">Action</th>
                      <th className="py-2 pr-4">By</th>
                      <th className="py-2 pr-4">Target</th>
                      <th className="py-2 pr-4">Client</th>
                      <th className="py-2 pr-4">From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e, i) => (
                      <tr key={`${e.timestamp}-${i}`} className="border-b last:border-0">
                        <td className="py-2 pr-4 whitespace-nowrap">{formatWhen(e.timestamp)}</td>
                        <td className="py-2 pr-4 font-mono text-xs">{e.action}</td>
                        <td className="py-2 pr-4">{e.actor?.email || '—'}</td>
                        <td className="py-2 pr-4">{e.target?.email || e.target?.user_id || e.target?.rules_id || '—'}</td>
                        <td className="py-2 pr-4 text-xs">{e.client_id || '—'}</td>
                        <td className="py-2 pr-4 text-xs">{e.request?.ip || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
